import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Button, useEntitled, useLadderShared, usePlan } from "@authe/ui-core";
import { NAV_ITEMS, ROUTES } from "@/config";
import { SETTINGS_NAV } from "@/features/settings/settingsNav";
import { ENTITLEMENT } from "@authe/fe-commons/plans";

/** Use the navigation contract for direct URLs too, before mounting data hooks. */
export function PlanRouteGate({ children }: { children: ReactNode }) {
  // location moves before a lazy destination is ready, while Outlet still
  // renders the previous match. Gate that rendered match throughout the
  // transition so leaving a withheld page cannot briefly mount its hooks.
  const pathname = useRouterState({ select: (state) => state.matches.at(-1)?.pathname ?? state.location.pathname });
  const entitled = useEntitled();
  const { accessVerified } = usePlan();
  const sharedLadder = useLadderShared();
  if (!sharedLadder && [ROUTES.settingsWorkspace, ROUTES.settingsStructure].some(to => pathname === to || pathname.startsWith(`${to}/`))) {
    return <section className="mx-auto max-w-xl rounded-xl border bg-card p-6">
      <h1 className="text-xl font-semibold">Your workspace is ready</h1>
      <p className="mt-2 text-sm text-muted-foreground">There is no workspace hierarchy to configure. You can continue exploring or change your personal settings.</p>
      <Button asChild className="mt-5"><Link to={ROUTES.settings}>Open settings</Link></Button>
    </section>;
  }
  const page = [...NAV_ITEMS, ...SETTINGS_NAV]
    .filter((item) => pathname === item.to || (item.to !== "/" && pathname.startsWith(`${item.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const withheld = page?.capability && !entitled(page.capability);
  if (!withheld) return children;
  if (accessVerified === false) return (
    <section className="mx-auto max-w-xl rounded-xl border bg-card p-6">
      <h1 className="text-xl font-semibold">Verifying subscription access</h1>
      <p className="mt-2 text-sm text-muted-foreground">This feature needs a verified subscription. You can retry or continue exploring while access is unavailable.</p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => window.location.reload()}>Retry</Button>
        <Button asChild variant="outline"><Link to={ROUTES.transactions}>Explore transactions</Link></Button>
      </div>
    </section>
  );
  const automaticDecoding = page.capability === ENTITLEMENT.CATALOG_CONFIDENCE_CONTROLS;
  return (
    <section className="mx-auto max-w-xl rounded-xl border bg-card p-6" aria-labelledby="plan-page-title">
      <h1 id="plan-page-title" className="text-xl font-semibold">{automaticDecoding ? "Decoding is already set up" : `${page.label} is a paid plan feature`}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {automaticDecoding
          ? "Free uses Authe’s standard decoding and confidence settings automatically. You can explore decoded transactions and see confidence warnings without configuring anything. Custom decoding and confidence settings are available on paid plans."
          : "Your plan includes transaction and address exploration, investigations, tracers, and basic annotations. View the plans to find the advanced tools you need."}
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button asChild><Link to={ROUTES.transactions}>Explore transactions</Link></Button>
        <Button asChild variant="outline"><Link to={ROUTES.settingsBilling}>View plans</Link></Button>
      </div>
    </section>
  );
}
